import {
  Checkbox,
  FormControlLabel,
  MenuItem,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { v4 } from '@rotra-air-ocean/rotranext-shared-code';

import { Stack } from '../../Stack';

import { StepSection } from './StepFormPrimitives';

const DEFAULT_PARSED_BINDING: v4.ParsedDocumentBinding = {
  source: 'currentEmail',
  selector: { type: 'all' },
  representation: 'parsedContent',
  preferredFormat: 'markdown',
};

function SelectorFields({
  selector,
  readOnly,
  onChange,
}: {
  selector: v4.WorkflowDocumentSelector;
  readOnly: boolean;
  onChange: (selector: v4.WorkflowDocumentSelector) => void;
}) {
  return (
    <>
      <TextField
        select
        label="Documents to use"
        value={selector.type}
        disabled={readOnly}
        onChange={(event) =>
          onChange(
            event.target.value === 'contextField'
              ? {
                  type: 'contextField',
                  field: '',
                  includeInline: selector.includeInline,
                }
              : { type: 'all', includeInline: selector.includeInline },
          )
        }
      >
        <MenuItem value="all">All eligible current-email documents</MenuItem>
        <MenuItem value="contextField">Attachment IDs from a field</MenuItem>
      </TextField>
      {selector.type === 'contextField' && (
        <TextField
          label="Attachment ID array field"
          value={selector.field}
          disabled={readOnly}
          helperText="Name of a workflow field containing an array of attachment IDs."
          onChange={(event) =>
            onChange({ ...selector, field: event.target.value })
          }
        />
      )}
      <FormControlLabel
        control={
          <Checkbox
            checked={selector.includeInline === true}
            disabled={readOnly}
            onChange={(event) =>
              onChange({ ...selector, includeInline: event.target.checked })
            }
          />
        }
        label="Include inline attachments"
      />
    </>
  );
}

export function ParsedDocumentBindingForm({
  value,
  readOnly,
  onChange,
}: {
  value?: v4.ParsedDocumentBinding;
  readOnly: boolean;
  onChange: (value: v4.ParsedDocumentBinding | undefined) => void;
}) {
  const binding = value ?? DEFAULT_PARSED_BINDING;
  return (
    <StepSection
      title="Documents"
      description="Load parsed current-email documents only while this AI step runs."
      optional
    >
      <FormControlLabel
        control={
          <Switch
            checked={Boolean(value)}
            disabled={readOnly}
            onChange={(event) =>
              onChange(
                event.target.checked ? DEFAULT_PARSED_BINDING : undefined,
              )
            }
          />
        }
        label="Use document content"
      />
      {value && (
        <>
          <SelectorFields
            selector={binding.selector}
            readOnly={readOnly}
            onChange={(selector) => onChange({ ...binding, selector })}
          />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <TextField
              type="number"
              label="Maximum documents"
              value={binding.maxDocuments ?? 10}
              disabled={readOnly}
              slotProps={{ htmlInput: { min: 1, max: 20 } }}
              onChange={(event) =>
                onChange({
                  ...binding,
                  maxDocuments: Number(event.target.value),
                })
              }
            />
            <TextField
              type="number"
              label="Total characters"
              value={binding.maxTotalCharacters ?? 100000}
              disabled={readOnly}
              slotProps={{ htmlInput: { min: 1, max: 200000 } }}
              onChange={(event) =>
                onChange({
                  ...binding,
                  maxTotalCharacters: Number(event.target.value),
                })
              }
            />
            <TextField
              type="number"
              label="Characters per document"
              value={binding.maxCharactersPerDocument ?? 50000}
              disabled={readOnly}
              slotProps={{ htmlInput: { min: 1, max: 100000 } }}
              onChange={(event) =>
                onChange({
                  ...binding,
                  maxCharactersPerDocument: Number(event.target.value),
                })
              }
            />
          </Stack>
        </>
      )}
    </StepSection>
  );
}

const representationLabels: Record<v4.ServiceDocumentRepresentation, string> = {
  metadata: 'Metadata only',
  parsedContent: 'Parsed document content',
  original: 'Original files',
};

export function ServiceDocumentBindingForm({
  value,
  capabilities,
  readOnly,
  onChange,
}: {
  value?: v4.ServiceDocumentBinding;
  capabilities: readonly v4.ServiceDocumentRepresentation[];
  readOnly: boolean;
  onChange: (value: v4.ServiceDocumentBinding | undefined) => void;
}) {
  const firstRepresentation = capabilities[0];
  const binding =
    value ??
    (firstRepresentation
      ? {
          source: 'currentEmail' as const,
          selector: { type: 'all' as const },
          representation: firstRepresentation,
        }
      : undefined);

  return (
    <StepSection
      title="Documents"
      description="Pass verified document metadata, parsed content, or original-file references to this service."
      optional
    >
      {capabilities.length === 0 && !value ? (
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
          }}
        >
          The selected service does not accept document bindings.
        </Typography>
      ) : (
        <>
          <FormControlLabel
            control={
              <Switch
                checked={Boolean(value)}
                disabled={readOnly || (!value && !firstRepresentation)}
                onChange={(event) =>
                  onChange(event.target.checked ? binding : undefined)
                }
              />
            }
            label="Send documents to this service"
          />
          {value && binding && (
            <>
              {!capabilities.includes(binding.representation) && (
                <Typography
                  variant="body2"
                  sx={{
                    color: 'warning.main',
                  }}
                >
                  The selected service no longer accepts this document
                  representation. Choose a supported representation or disable
                  the binding.
                </Typography>
              )}
              <TextField
                select
                label="Document representation"
                value={binding.representation}
                disabled={readOnly}
                onChange={(event) =>
                  onChange({
                    ...binding,
                    representation: event.target
                      .value as v4.ServiceDocumentRepresentation,
                  })
                }
              >
                {capabilities.map((representation) => (
                  <MenuItem key={representation} value={representation}>
                    {representationLabels[representation]}
                  </MenuItem>
                ))}
              </TextField>
              <SelectorFields
                selector={binding.selector}
                readOnly={readOnly}
                onChange={(selector) => onChange({ ...binding, selector })}
              />
            </>
          )}
        </>
      )}
    </StepSection>
  );
}
